import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { downloadCSV } from '../utils/fileExport';
import toast from 'react-hot-toast';
import CsvImportModal from '../components/CsvImportModal';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import PortfolioRequired from '../components/PortfolioRequired';
import { GET_ALL_TOKENS } from '../graphql/queries/tokens';
import { CREATE_TOKEN_GROUP, SET_TOKEN_GROUP, RENAME_TOKEN_GROUP, DELETE_TOKEN_GROUP, IMPORT_TOKEN_GROUPS_CSV } from '../graphql/mutations/tokenGroups';

const GroupsPage = () => {
  const { currentPortfolio } = usePortfolioContext();
  const portfolioId = currentPortfolio?.id;

  const [newGroupName, setNewGroupName] = useState('');
  const [isAddingForGroupId, setIsAddingForGroupId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [importing] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [tempGroupName, setTempGroupName] = useState('');
  const [deleteGroupTarget, setDeleteGroupTarget] = useState(null);
  const [savingGroupId, setSavingGroupId] = useState(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef(null);

  const { data, loading, error, refetch } = useQuery(GET_ALL_TOKENS, {
    variables: { portfolioId },
    skip: !portfolioId,
    fetchPolicy: 'cache-and-network'
  });

  const [createGroup, { loading: creating }] = useMutation(CREATE_TOKEN_GROUP, {
    onCompleted: () => {
      toast.success('Group created');
      setNewGroupName('');
      refetch();
    },
    onError: (e) => toast.error(e.message)
  });

  const [setTokenGroup] = useMutation(SET_TOKEN_GROUP, {
    onCompleted: () => {
      toast.success('Updated token group');
      refetch();
    },
    onError: (e) => toast.error(e.message)
  });

  const [renameTokenGroup] = useMutation(RENAME_TOKEN_GROUP, {
    onError: (e) => toast.error(e.message)
  });

  const [deleteTokenGroup] = useMutation(DELETE_TOKEN_GROUP, {
    onError: (e) => toast.error(e.message)
  });

  const [importTokenGroupsCSV] = useMutation(IMPORT_TOKEN_GROUPS_CSV);

  // Local-only search: filter the tokens we have from GET_ALL_TOKENS

  const tokens = useMemo(() => (data?.tokens || []), [data]);
  const groups = useMemo(() => (data?.tokenGroups || []), [data]);

  const groupedTokens = useMemo(() => {
    const byGroupId = new Map();
    groups.forEach(g => byGroupId.set(g.id, []));
    const ungrouped = [];
    tokens.forEach(t => {
      const gid = t.group?.id || null;
      if (gid && byGroupId.has(gid)) {
        byGroupId.get(gid).push(t);
      } else {
        ungrouped.push(t);
      }
    });
    return { byGroupId, ungrouped };
  }, [tokens, groups]);
  const hasUngrouped = groupedTokens.ungrouped.length > 0;

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim() || !portfolioId) return;
    await createGroup({ variables: { portfolioId, name: newGroupName.trim() } });
  };

  const handleRemoveFromGroup = async (tokenId) => {
    if (!portfolioId) return;
    await setTokenGroup({ variables: { portfolioId, tokenId, groupId: null } });
  };

  const handleOpenAddToken = (groupId) => {
    setIsAddingForGroupId(groupId);
    setSearchTerm('');
    setSelectedIndex(0);
  };

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setSelectedIndex(0);
  };

  const handleSelectSearchToken = async (token) => {
    if (!portfolioId || isAddingForGroupId === undefined) return;
    await setTokenGroup({ variables: { portfolioId, tokenId: token.id, groupId: isAddingForGroupId } });
    setIsAddingForGroupId(null);
    setSearchTerm('');
  };

  // Compute filtered local tokens based on search term
  const filteredLocalResults = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return [];
    return tokens
      .filter(t => t.name.toLowerCase().includes(term) || t.ticker.toLowerCase().includes(term))
      .slice(0, 25);
  }, [tokens, searchTerm]);

  // Focus input when the add-token modal opens
  useEffect(() => {
    if (isAddingForGroupId !== null && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current && searchInputRef.current.focus();
      }, 10);
    }
  }, [isAddingForGroupId]);

  // Keyboard navigation within the add-token modal
  const handleAddTokenKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setIsAddingForGroupId(null);
      return;
    }
    if (filteredLocalResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, filteredLocalResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selected = filteredLocalResults[selectedIndex];
      if (selected) {
        handleSelectSearchToken(selected);
      }
    }
  };

  // DnD helpers
  const onDragStart = useCallback((e, tokenId) => {
    e.dataTransfer.setData('text/plain', tokenId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const onDropToGroup = useCallback(async (e, targetGroupId) => {
    e.preventDefault();
    const tokenId = e.dataTransfer.getData('text/plain');
    if (!tokenId) return;
    await setTokenGroup({ variables: { portfolioId, tokenId, groupId: targetGroupId } });
  }, [portfolioId, setTokenGroup]);

  const onDropToUngrouped = useCallback(async (e) => {
    e.preventDefault();
    const tokenId = e.dataTransfer.getData('text/plain');
    if (!tokenId) return;
    await setTokenGroup({ variables: { portfolioId, tokenId, groupId: null } });
  }, [portfolioId, setTokenGroup]);

  const startEditGroup = (group) => {
    setEditingGroupId(group.id);
    setTempGroupName(group.name || '');
  };

  const cancelEditGroup = () => {
    setEditingGroupId(null);
    setTempGroupName('');
  };

  const saveEditGroup = async (group) => {
    if (savingGroupId === group.id) return;
    const trimmed = (tempGroupName || '').trim();
    if (!trimmed || trimmed === group.name) {
      cancelEditGroup();
      return;
    }
    setSavingGroupId(group.id);
    try {
      await renameTokenGroup({ variables: { portfolioId, groupId: group.id, name: trimmed } });
      toast.success('Group renamed');
      await refetch();
    } finally {
      cancelEditGroup();
      setSavingGroupId(null);
    }
  };

  const requestDeleteGroup = (group) => {
    setDeleteGroupTarget(group);
  };

  const confirmDeleteGroup = async () => {
    if (!deleteGroupTarget) return;
    const group = deleteGroupTarget;
    await deleteTokenGroup({ variables: { portfolioId, groupId: group.id } });
    toast.success('Group deleted');
    setDeleteGroupTarget(null);
    await refetch();
  };

  const cancelDeleteGroup = () => setDeleteGroupTarget(null);

  const handleExport = () => {
    const header = 'CoingeckoId,Group\n';
    const lines = tokens
      .filter(t => t.group && t.group.name)
      .map(t => {
        const gid = t.group.name;
        const cid = t.coingeckoApiId || '';
        return `${cid},${gid}`;
      });
    const csv = header + lines.join('\n');
    downloadCSV(csv, currentPortfolio?.name, 'groups');
  };

  const openImportModal = () => setIsImportModalOpen(true);
  const closeImportModal = () => setIsImportModalOpen(false);

  const importChunk = async (csvChunk) => {
    const resp = await importTokenGroupsCSV({ variables: { portfolioId, csvData: csvChunk } });
    return resp.data?.importTokenGroupsCSV;
  };

  if (!portfolioId) {
    return <PortfolioRequired title="Groups" description="Select a portfolio to manage groups." />;
  }

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <div className="text-red-500">{error.message}</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Groups</h1>
        <div className="flex items-center gap-2">
          <button onClick={openImportModal} disabled={importing} className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none disabled:opacity-50">Import CSV</button>
          <button onClick={handleExport} className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none">Export CSV</button>
        </div>
      </div>

      <form onSubmit={handleCreateGroup} className="flex items-center gap-2">
        <input
          type="text"
          placeholder="New group name"
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          className="flex-1 px-3 py-2 border rounded dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
        />
        <button type="submit" disabled={!newGroupName.trim() || creating}
          className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none disabled:opacity-50">Create</button>
      </form>

      {/* Ungrouped section */}
      <div className={`border rounded-lg dark:border-gray-700 ${hasUngrouped ? 'sticky top-0 z-30 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm' : ''}`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDropToUngrouped}
      >
        <div className="px-4 py-3 border-b flex items_center justify-between dark:border-gray-700">
          <div className="font-semibold dark:text-gray-100">Ungrouped</div>
        </div>
        <div className="p-4 flex flex-wrap gap-2">
          {groupedTokens.ungrouped.map(t => (
            <div
              key={t.id}
              title={t.name}
              draggable
              onDragStart={(e) => onDragStart(e, t.id)}
              className="group inline-flex items-center gap-1 px-2 py-1 text-sm border rounded-full dark:border-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <span className="font-mono">{t.ticker}</span>
            </div>
          ))}
          {groupedTokens.ungrouped.length === 0 && (
            <div className="text-sm text-gray-500 dark:text-gray-400">No tokens</div>
          )}
        </div>
      </div>

      {/* Group sections */}
      {groups.map(group => {
        return (
        <div key={group.id} className="border rounded-lg dark:border-gray-700"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => onDropToGroup(e, group.id)}
        >
          <div className="px-4 py-3 border-b flex items-center justify-between dark:border-gray-700">
            <div className="font-semibold dark:text-gray-100">
              {editingGroupId === group.id ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={tempGroupName}
                    onChange={(e) => setTempGroupName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEditGroup(group);
                      else if (e.key === 'Escape') cancelEditGroup();
                    }}
                    onBlur={() => saveEditGroup(group)}
                    className="bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 px-1"
                    autoFocus
                  />
                </div>
              ) : (
                <button
                  className="text-left"
                  onClick={() => startEditGroup(group)}
                  title="Rename group"
                >
                  {group.name}
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => handleOpenAddToken(group.id)} className="px-2 py-1 text-sm rounded bg-gray-200 dark:bg-gray-700 dark:text-gray-100">Add token</button>
              <button onClick={() => requestDeleteGroup(group)} className="px-2 py-1 text-sm rounded bg-red-600 text-white">Delete</button>
            </div>
          </div>
          <div className="p-4 flex flex-wrap gap-2">
            {(groupedTokens.byGroupId.get(group.id) || []).map(t => (
              <div
                key={t.id}
                title={t.name}
                draggable
                onDragStart={(e) => onDragStart(e, t.id)}
                className="group inline-flex items-center gap-1 px-2 py-1 text-sm border rounded-full dark:border-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <span className="font-mono">{t.ticker}</span>
                <button
                  onClick={() => handleRemoveFromGroup(t.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-xs px-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30"
                  aria-label="Remove from group"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      );})}

      {/* Add token panel */}
      {isAddingForGroupId !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w_full max-w-lg">
            <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
              <div className="font-semibold dark:text-gray-100">Add token</div>
              <button onClick={() => setIsAddingForGroupId(null)} className="text-gray-500">✕</button>
            </div>
            <div className="p-4 space-y-3">
              <input
                type="text"
                placeholder="Search tokens..."
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleAddTokenKeyDown}
                ref={searchInputRef}
                className="w-full px-3 py-2 border rounded dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100"
              />
              <div className="max-h-72 overflow-y-auto divide-y dark:divide-gray-700">
                {filteredLocalResults.map((t, idx) => (
                  <button key={t.id} onClick={() => handleSelectSearchToken(t)}
                    className={`w-full text-left px-3 py-2 dark:text-gray-100 ${idx === selectedIndex ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                  >
                    {t.name} ({t.ticker})
                  </button>
                ))}
                {searchTerm.length > 0 && filteredLocalResults.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">No results</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteGroupTarget && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Delete Group</h2>
              <button onClick={cancelDeleteGroup} className="text-gray-500 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300">
                Are you sure you want to delete the group "{deleteGroupTarget?.name}"? Tokens in this group will be ungrouped. This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={cancelDeleteGroup} className="mr-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded" >
                Cancel
              </button>
              <button type="button" onClick={confirmDeleteGroup} className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import modal */}
      {isImportModalOpen && (
        <CsvImportModal
          isOpen={isImportModalOpen}
          onClose={closeImportModal}
          title="Import Groups"
          placeholder="CoingeckoId,Group..."
          importChunk={importChunk}
          onSuccess={async () => { await refetch(); }}
        />
      )}
    </div>
  );
};

export default GroupsPage;


